import React, { useState } from 'react';
import { bool } from 'prop-types';
import { intlShape } from '../../../util/reactIntl';
import { Avatar, IconSpinner } from '../../../components';
import { FILE_MESSAGE_TEXT_CONTENT } from '../../../util/transaction';
import { propTypes } from '../../../util/types';
import { resolveFileMessageType } from '../../../util/transaction-messages';
import css from './Message.css';
import MessageMeta from './MessageMeta';
import ContactInfo from './ContactInfo';
import classNames from 'classnames';
import OrdinaryMessageContent from './OrdinaryMessageContent';
import { useCopyToClickBoard } from '../../../hooks/useCopyToClickBoard';
import { MessageContext } from './useMessageContext';
import FileMessage from './FileMessage';
import { useDispatch, useSelector } from 'react-redux';
import { deleteMessage as deleteMessageThunk } from '../../../containers/InboxPage/InboxPage.duck';

const Message = props => {
    const {
        message,
        isOwnMessage,
        handleImageCarouselVisibility,
        messageIsViewedByOtherParty,
        otherParty,
        isContactInfoSharing,
        currentTransaction,
    } = props;
    const dispatch = useDispatch();

    const { copied, copyToClickBoard } = useCopyToClickBoard({ resetAfterMilSeconds: 2000 });

    const deleteMessageInProgress = useSelector(s => s['InboxPage']?.deleteMessageInProgress);
    const deleteMessagePendingId = useSelector(s => s['InboxPage']?.deleteMessagePendingId);

    const [controlsPanelOpen, setControlsVisibility] = useState(false);

    const { attributes, sender } = message || {};
    const { file, content } = attributes || {};

    const currentTransactionId = currentTransaction?.id?.uuid;
    const messageId = message?.id?.uuid;
    const fileTextContent = content === FILE_MESSAGE_TEXT_CONTENT;

    const { fileIsDoc, fileIsImage } = resolveFileMessageType(message);

    const messageContentClass = isOwnMessage ? css.ownMessageContent : css.messageContent;
    const messageContentWrapper = isOwnMessage ? css.ownMessageContentWrapper : css.contentWrapper;
    const imageClassName = isOwnMessage ? css.s3OwnImage : css.s3Image;
    const showTextContent = !fileTextContent && !isContactInfoSharing;
    const deleteMessageAllowed = showTextContent && !messageIsViewedByOtherParty && isOwnMessage;
    const isPendingToDeleteMessage = deleteMessagePendingId === messageId;
    const isMessageDeleted = Boolean(attributes?.isDeleted);

    const imageClickHandler = () => fileIsImage && handleImageCarouselVisibility(file.url);

    const handleMessageDeleting = () => {
        setControlsVisibility(false);
        dispatch(deleteMessageThunk(currentTransactionId, messageId));
    };

    if (deleteMessageInProgress && isPendingToDeleteMessage) {
        return <IconSpinner />;
    }

    return (
        <MessageContext.Provider
            value={{
                message,
                fileIsDoc,
                isOwnMessage,
                fileIsImage,
                copied,
                controlsPanelOpen,
                messageIsViewedByOtherParty,
                deleteMessageAllowed,
                isPendingToDeleteMessage,
                isMessageDeleted,
                copyToClickBoard,
                setControlsVisibility: value => {
                    if (!isMessageDeleted) {
                        setControlsVisibility(value);
                    }
                },
                handleMessageDeleting,
            }}
        >
            <div
                className={classNames({
                    [css.ownMessage]: isOwnMessage,
                    [css.message]: !isOwnMessage,
                    [css.contactInfoMessage]: isContactInfoSharing,
                })}
                onClick={imageClickHandler}
            >
                {!isOwnMessage && <Avatar className={css.avatar} user={otherParty} />}
                <div className={messageContentWrapper}>
                    {showTextContent && (
                        <OrdinaryMessageContent
                            messageContentClassName={classNames({
                                [css.ownMessageContent]: isOwnMessage,
                                [css.messageContent]: !isOwnMessage,
                            })}
                        />
                    )}

                    {Boolean(fileIsDoc || fileIsImage) && (
                        <FileMessage imageClassName={imageClassName} />
                    )}

                    {isContactInfoSharing && (
                        <div className={classNames(messageContentClass, css.contactInfo)}>
                            <ContactInfo {...props} />
                        </div>
                    )}

                    <MessageMeta />
                </div>
            </div>
        </MessageContext.Provider>
    );
};

export default Message;
